import React, { useState } from 'react';
import { X, AlertTriangle, Loader2, CheckCircle, XCircle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { adminApi } from '../../services/api';
import { TenantRecord, TenantDeletionCheck, TenantDeletionResult } from '../../types';

interface DeleteTenantModalProps {
  tenant: TenantRecord;
  onClose: () => void;
  onSuccess: () => void;
}

type DeletionStep = 'preflight' | 'confirm' | 'deleting' | 'complete';

const DeleteTenantModal: React.FC<DeleteTenantModalProps> = ({ tenant, onClose, onSuccess }) => {
  const [step, setStep] = useState<DeletionStep>('preflight');
  const [preflightCheck, setPreflightCheck] = useState<TenantDeletionCheck | null>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletionResult, setDeletionResult] = useState<TenantDeletionResult | null>(null);

  React.useEffect(() => {
    if (step === 'preflight') {
      checkDeletionEligibility();
    }
  }, [step]);

  const checkDeletionEligibility = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminApi.checkTenantDeletion(tenant.tenantId);
      setPreflightCheck(result);
    } catch (err: any) {
      setError(err.message || 'Failed to check deletion eligibility');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);
      setStep('deleting');
      
      const result = await adminApi.deleteTenant(tenant.tenantId);
      setDeletionResult(result);
      setStep('complete');
      
      // Auto-close and refresh after 3 seconds
      setTimeout(() => {
        onSuccess();
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete tenant');
      setStep('confirm'); // Go back to confirmation
    } finally {
      setLoading(false);
    }
  };

  const canProceed = confirmationText === tenant.tenantId && understood;

  return (
    <Modal isOpen onClose={onClose} title="">
      <div className="p-6">
        {/* Preflight Check Step */}
        {step === 'preflight' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Delete Tenant: {tenant.name}
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  Checking deletion eligibility...
                </p>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : error ? (
              <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            ) : preflightCheck ? (
              <>
                <div className="space-y-4 mb-6">
                  <div className="p-4 rounded-lg border dark:border-gray-700 border-gray-200">
                    <h3 className="font-semibold dark:text-white text-gray-900 mb-2">
                      Deletion Impact Assessment
                    </h3>
                    <p className="text-sm dark:text-gray-400 text-gray-600 mb-3">
                      {preflightCheck.reason}
                    </p>
                    
                    {preflightCheck.warnings.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium dark:text-yellow-400 text-yellow-700">
                          Warnings:
                        </p>
                        <ul className="space-y-1">
                          {preflightCheck.warnings.map((warning, index) => (
                            <li key={index} className="text-sm dark:text-gray-300 text-gray-700 flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                              <span>{warning}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">
                      ⚠️ This action will permanently:
                    </h4>
                    <ul className="space-y-1 text-sm text-red-700 dark:text-red-300">
                      <li>• Drop the tenant's entire MongoDB database</li>
                      <li>• Remove all users from the global directory</li>
                      <li>• Close all database connections</li>
                      <li>• Delete the tenant record</li>
                      <li>• <strong>This cannot be undone</strong></li>
                    </ul>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="secondary" onClick={onClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => setStep('confirm')}
                    className="flex-1"
                    disabled={!preflightCheck.canDelete}
                  >
                    {preflightCheck.canDelete ? 'Continue to Confirmation' : 'Cannot Delete'}
                  </Button>
                </div>
              </>
            ) : null}
          </>
        )}

        {/* Confirmation Step */}
        {step === 'confirm' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Confirm Deletion
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  Type the tenant ID to confirm
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">
                  Type <span className="font-mono font-bold">{tenant.tenantId}</span> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder={tenant.tenantId}
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono"
                  autoFocus
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={understood}
                  onChange={(e) => setUnderstood(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <span className="text-sm dark:text-gray-300 text-gray-700">
                  I understand this action is <strong>permanent and irreversible</strong>. All data will be lost.
                </span>
              </label>
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setStep('preflight')} className="flex-1">
                Back
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                className="flex-1"
                disabled={!canProceed || loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Tenant Permanently'
                )}
              </Button>
            </div>
          </>
        )}

        {/* Deleting Progress */}
        {step === 'deleting' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Deleting Tenant...
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  Please wait while we remove all data
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {['user_directory_cleanup', 'database_drop', 'connection_cleanup', 'tenant_record_removal'].map((stepName) => (
                <div key={stepName} className="flex items-center gap-3 p-3 rounded-lg border dark:border-gray-700 border-gray-200">
                  <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                  <span className="text-sm dark:text-gray-300 text-gray-700">
                    {stepName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}...
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Complete Step */}
        {step === 'complete' && deletionResult && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Tenant Deleted
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  All data has been removed successfully
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {deletionResult.steps.map((step, index) => (
                <div key={index} className="flex items-center gap-3 p-3 rounded-lg border dark:border-gray-700 border-gray-200">
                  {step.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  )}
                  <span className="text-sm dark:text-gray-300 text-gray-700">
                    {step.step.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
              ))}
            </div>

            <p className="text-sm text-center dark:text-gray-400 text-gray-600 mb-4">
              Closing automatically in 3 seconds...
            </p>

            <Button onClick={onSuccess} className="w-full">
              Close
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default DeleteTenantModal;
